import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { plainToInstance } from 'class-transformer';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';

import { JSend } from '../../models/jsend.model';
import { Company } from '../../models/company.model';
import { TorresCredentials } from '../models/torres-credentials.model';

/** Shape of the "interviniente" as stored/returned by the backend. */
interface TorresIntermediaryCompanyDTO {
  company_id: number;
  fiscal_id: string | null;
}

@Injectable({
  providedIn: 'root'
})
export class TorresService {

  private baseUrl = '/:apiBase/companies/:companyId/torres/credentials';

  constructor(
    private http: HttpClient
  ) { }

  public get(companyId: number): Observable<TorresCredentials> {
    const url = this.baseUrl.replace(':companyId', companyId.toString());

    return this.http.get<JSend<{
      credentials: TorresCredentials & { intermediary_company?: TorresIntermediaryCompanyDTO };
    }>>(url).pipe(map(response => this.toModel(response.data.credentials)));
  }

  public save(companyId: number, credentials: TorresCredentials): Observable<TorresCredentials> {
    const url = this.baseUrl.replace(':companyId', companyId.toString());

    return this.http.put<JSend<{
      credentials: TorresCredentials & { intermediary_company?: TorresIntermediaryCompanyDTO };
    }>>(url, this.toPayload(credentials)).pipe(map(response => this.toModel(response.data.credentials)));
  }

  /**
   * Maps the front-end model to the payload shape expected by the backend:
   * the full `intermediary` Company is reduced to `intermediary_company:
   * { company_id, fiscal_id }`. When no intermediary is selected (it's an
   * optional field) the key is omitted entirely.
   */
  private toPayload(credentials: TorresCredentials): any {
    const payload: any = {
      apiKey: credentials.apiKey
    };

    if (credentials.intermediary) {
      payload.intermediary_company = {
        company_id: credentials.intermediary.id,
        fiscal_id: credentials.intermediary.fiscal_id?.value ?? null
      };
    }

    return payload;
  }

  /**
   * Rebuilds the front-end model from the backend response. The backend only
   * returns `intermediary_company: { company_id, fiscal_id }`, without the
   * Company's name or other details, so we reconstruct a minimal partial
   * `Company` (cast) with just the id and fiscal id. This is an accepted
   * limitation: if the full Company (e.g. its name) is ever needed here, it
   * should be resolved separately (e.g. fetching the company by id in the
   * component) instead of being carried by this endpoint's response.
   */
  private toModel(data: TorresCredentials & { intermediary_company?: TorresIntermediaryCompanyDTO }): TorresCredentials {
    const credentials = plainToInstance(TorresCredentials, {
      apiKey: data?.apiKey
    });

    if (data?.intermediary_company) {
      credentials.intermediary = {
        id: data.intermediary_company.company_id,
        fiscal_id: { value: data.intermediary_company.fiscal_id }
      } as Company;
    }

    return credentials;
  }
}
